/**
 * Shared role search/filtering logic extracted from job_role_autocomplete_controller.
 * Used by both admin and public autocomplete controllers.
 */

function escapeRegex(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function isWholeWordMatch(text, word) {
  const regex = new RegExp(`(^|[\\s/()\\-])${escapeRegex(word)}($|[\\s/()\\-])`, 'i')
  return regex.test(text)
}

function matchesAlias(aliases, word) {
  if (!aliases || aliases.length === 0) return { whole: false, substring: false }
  const aliasText = aliases.join(' ').toLowerCase()
  return {
    whole: isWholeWordMatch(aliasText, word),
    substring: aliasText.includes(word)
  }
}

function getMatchLocation(word, title, roleName, careerFamily, aliases) {
  if (isWholeWordMatch(title, word)) return 'title_whole'
  if (title.startsWith(word)) return 'title_starts'
  if (title.includes(word)) return 'title_substring'

  const aliasMatch = matchesAlias(aliases, word)
  if (aliasMatch.whole) return 'alias_whole'

  if (isWholeWordMatch(roleName, word)) return 'role_whole'
  if (roleName.includes(word)) return 'role_substring'

  if (aliasMatch.substring) return 'alias_substring'

  if (careerFamily.includes(word)) return 'family_only'

  return 'none'
}

/**
 * Filter and sort roles by query relevance.
 * @param {Array} roles - Array of role objects with canonical_title, career_family, search_count, aliases
 * @param {string} query - Search query string
 * @param {number} limit - Max results to return (default 10)
 * @returns {Array} Sorted, filtered roles
 */
export function filterAndSortRoles(roles, query, limit = 10) {
  const queryLower = query.trim().toLowerCase()
  if (queryLower.length < 2) return []

  const queryWords = queryLower.split(/\s+/).filter(w => w.length > 0)

  const scored = roles
    .map(role => {
      const title = role.canonical_title.toLowerCase()
      const roleName = (role.role_name || "").toLowerCase()
      const careerFamily = (role.career_family || "").toLowerCase()
      const aliases = role.aliases || []

      const matchLocations = queryWords.map(word =>
        getMatchLocation(word, title, roleName, careerFamily, aliases)
      )

      if (matchLocations.includes('none')) {
        return { role, score: 0 }
      }

      let titleWholeMatches = 0
      let titleStartsMatches = 0
      let titleSubstringMatches = 0
      let aliasWholeMatches = 0
      let aliasSubstringMatches = 0
      let roleWholeMatches = 0
      let roleSubstringMatches = 0
      let familyOnlyMatches = 0

      for (const loc of matchLocations) {
        switch (loc) {
          case 'title_whole': titleWholeMatches++; break
          case 'title_starts': titleStartsMatches++; break
          case 'title_substring': titleSubstringMatches++; break
          case 'alias_whole': aliasWholeMatches++; break
          case 'alias_substring': aliasSubstringMatches++; break
          case 'role_whole': roleWholeMatches++; break
          case 'role_substring': roleSubstringMatches++; break
          case 'family_only': familyOnlyMatches++; break
        }
      }

      const firstWordLoc = matchLocations[0]
      let score = 0

      if (title === queryLower) {
        score = 1000
      } else if (firstWordLoc === 'title_whole' && title.startsWith(queryWords[0] + " ")) {
        score = 900
      } else if (firstWordLoc === 'title_whole') {
        score = 800
      } else if (firstWordLoc === 'title_starts') {
        score = 700
      } else if (firstWordLoc === 'title_substring') {
        score = 500
      } else if (firstWordLoc === 'alias_whole') {
        score = 650
      } else if (firstWordLoc === 'role_whole') {
        score = 600
      } else if (firstWordLoc === 'alias_substring') {
        score = 450
      } else if (firstWordLoc === 'role_substring') {
        score = 400
      } else if (firstWordLoc === 'family_only') {
        score = 100
      }

      const goodMatches = titleWholeMatches + titleStartsMatches + titleSubstringMatches +
                          aliasWholeMatches + aliasSubstringMatches +
                          roleWholeMatches + roleSubstringMatches
      score += goodMatches * 50
      score -= familyOnlyMatches * 100
      score += (titleWholeMatches + roleWholeMatches + aliasWholeMatches) * 30
      score += Math.max(0, 50 - title.length)
      score += (role.search_count || 0) / 10000
      if (role.has_salary_data) score += 200

      return { role, score }
    })
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(item => item.role)

  return scored
}

export function escapeHtml(text) {
  const div = document.createElement("div")
  div.textContent = text
  return div.innerHTML
}
